import { Component, computed, inject, input, OnInit, output, signal, TemplateRef, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../../core/utils";
import { noWhitespaceValidator } from "../../../../core/validators";
import { DataTableComponent, FormDropdownComponent, FormInputComponent } from "../../../../shared/components";
import { Constants } from "../../../../shared/constants/constants";
import { PageMode } from "../../../../shared/constants/enums";
import {
  GovScoresPillar,
  PillarFocus,
  PropTypeModelType,
  TableButtonClicked,
  ValueText,
} from "../../../../shared/models";
import { GovScoresApiService, LanguageService } from "../../../../shared/services";
import { AreaOfFocusModalComponent } from "../area-of-focus-modal/area-of-focus-modal";

@Component({
  selector: "adaa-entity-pillars",
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    FormInputComponent,
    FormDropdownComponent,
    DataTableComponent,
    AreaOfFocusModalComponent,
  ],
  templateUrl: "./entity-pillars-types-modal.html",
  styleUrl: "./entity-pillars-types-modal.scss",
})
export class EntityPillarsTypesModalComponent implements OnInit {
  private _modalService = inject(NgbModal);
  private _languageService = inject(LanguageService);
  private _toastrService = inject(ToastrService);
  private _formBuilder = inject(FormBuilder);
  private _govScoresApiService = inject(GovScoresApiService);

  entityPillarForm: FormGroup = new FormGroup({});
  submitted = signal<boolean>(false);
  focusList = signal<PillarFocus[]>([]);
  modal = viewChild.required<TemplateRef<unknown>>("content");
  areaOfFocus = viewChild.required<AreaOfFocusModalComponent>("focus");
  pageMode = input<string>();
  pillars = input<GovScoresPillar[]>();
  focusIndex: number | null;
  pillarIndex: number;
  Constants = Constants;
  PageMode = PageMode;
  pillar = signal<Partial<GovScoresPillar>>({});
  pillarOutput = output<Partial<GovScoresPillar>>();
  modalRef: NgbModalRef;

  pillarTypes = signal<PropTypeModelType[]>([]);
  pillarTypeOptions = computed(() => {
    const field = AdaaHelper.getFieldLanguage("name");
    const result: ValueText[] = AdaaHelper.setDropdownArray(this.pillarTypes(), "id", field);
    return result;
  });

  public ngOnInit(): void {
    this._prepareForm();
    this._govScoresApiService.getAvailablePillarTypes().subscribe({
      next: (response) => this.pillarTypes.set(response.responseData),
    });
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.entityPillarForm = this._formBuilder.group({
      pillarType: this._formBuilder.control<number | null>(
        { value: null, disabled: isViewMode },
        {
          validators: [Validators.required, noWhitespaceValidator()],
        }
      ),
      weight: this._formBuilder.control<number | null>(
        { value: null, disabled: isViewMode },
        {
          validators: [Validators.required, noWhitespaceValidator()],
        }
      ),
      summary: this._formBuilder.control<string>({ value: "", disabled: isViewMode }),
    });
  }

  public open() {
    const modalRef = this._modalService.open(this.modal(), {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "xl",
      centered: true,
      modalDialogClass: this._languageService.modalDirection(),
    });

    this.modalRef = modalRef;
    modalRef.dismissed.subscribe(() => {
      this._resetForm();
      this.focusList.set([]);
    });
    modalRef.closed.subscribe(() => {
      this._resetForm();
      this.focusList.set([]);
    });
  }

  public submit() {
    this.submitted.set(true);
    if (this.entityPillarForm.invalid) {
      this._toastrService.error(this._languageService.translate("notification.warning.missing_info"));
      return;
    }

    const result = {
      ...this.entityPillarForm.value,
      areasOfFocusList: this.focusList(),
    };
    const pillar = this.pillarTypes().find((item) => item.id === result.pillarType);
    result["pillarTypeNameAE"] = pillar?.nameAE;
    result["pillarTypeNameEN"] = pillar?.nameEN;

    this.pillarOutput.emit(result);
    this.modalRef.close();
  }

  private _resetForm() {
    this.entityPillarForm.reset();
    this.entityPillarForm.markAsPristine();
    this.entityPillarForm.markAsUntouched();
    this.submitted.set(false);
  }

  public addFocusList(focus: PillarFocus) {
    const focusList: PillarFocus[] = AdaaHelper.clone(this.focusList());
    if (AdaaHelper.isDefinedAndNotEmpty(this.focusIndex)) {
      focusList.splice(this.focusIndex, 1, focus);
    } else focusList.push(focus);
    this.focusList.set(focusList);
  }

  public getAction(value: TableButtonClicked) {
    if (AdaaHelper.isDefinedAndNotEmpty(value.index)) this.focusIndex = value.index;
    switch (value.event) {
      case "view":
      case "edit":
        this.areaOfFocus().areaOfFocusForm.patchValue(value.data);
        AdaaHelper.isDefinedAndNotEmpty(value.index) && (this.areaOfFocus().focusIndex = value.index);
        this.areaOfFocus().open();
        break;
      case "delete": {
        const list: PillarFocus[] = AdaaHelper.clone(this.focusList());
        list.splice(this.focusIndex as number, 1);
        this.focusList.set(list);
        break;
      }

      default:
        break;
    }
  }
}
